import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { projectApi, departmentApi, sectorApi, userApi, flagsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { ScrollArea } from '../components/ui/scroll-area';
import { 
  format, 
  startOfMonth, 
  endOfMonth, 
  startOfQuarter, 
  endOfQuarter, 
  startOfYear, 
  endOfYear, 
  eachMonthOfInterval, 
  eachWeekOfInterval,
  addMonths,
  subMonths,
  addQuarters,
  subQuarters,
  addYears,
  subYears,
  differenceInDays,
  parseISO,
  isWithinInterval
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  rectSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { 
  Plus, 
  FolderKanban, 
  Trash2, 
  ArrowRight,
  Loader2,
  Building,
  Users,
  Settings2,
  Eye,
  EyeOff,
  Lock,
  Globe,
  Pencil,
  LayoutGrid,
  List,
  Search,
  Filter,
  ExternalLink,
  Calendar,
  CalendarDays,
  CheckCircle2,
  Clock,
  Briefcase,
  Music,
  Cpu,
  DollarSign,
  Palette,
  Megaphone,
  Wrench,
  FileText,
  BarChart3,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  CalendarClock,
  Layers,
  ChevronRight,
  ChevronLeft,
  ChevronUp,
  ChevronDown,
  EyeOff as EyeOffIcon,
  GripVertical,
  Flag,
  PlayCircle,
  RotateCcw,
  Star,
  Heart,
  Zap,
  Target,
  Bookmark,
  Award,
  Lightbulb,
  Send,
  Package,
  Shield,
  Home,
  Phone,
  Mail,
  Sun,
  Sparkles,
  Rocket,
  Flame,
  Leaf,
  Cloud,
  Moon,
  Compass,
  Gem,
  Crown,
  Coffee,
  Gift,
  Headphones,
  Camera,
  Mic,
  Video,
  Film,
  Tv,
  Radio,
  Gamepad2,
  Plane,
  Car,
  Ship,
  MapPin,
  Mountain,
  Waves,
  TreePine,
  Flower2,
} from 'lucide-react';
import { toast } from 'sonner';

// Opções de cores para grupos - estilo VibeDaY
const DEPT_COLOR_OPTIONS = [
  // Cores vibrantes (VibeDaY style)
  { value: '#F97316', name: 'Laranja', class: 'bg-orange-500' },
  { value: '#8B5CF6', name: 'Violeta', class: 'bg-violet-500' },
  { value: '#EC4899', name: 'Rosa', class: 'bg-blue-500' },
  { value: '#F59E0B', name: 'Âmbar', class: 'bg-amber-500' },
  { value: '#EF4444', name: 'Vermelho', class: 'bg-red-500' },
  
  // Cores naturais
  { value: '#10B981', name: 'Esmeralda', class: 'bg-emerald-500' },
  { value: '#3B82F6', name: 'Azul', class: 'bg-blue-500' },
  { value: '#06B6D4', name: 'Ciano', class: 'bg-cyan-500' },
  { value: '#84CC16', name: 'Lima', class: 'bg-lime-500' },
  { value: '#14B8A6', name: 'Teal', class: 'bg-teal-500' },
  
  // Tons neutros
  { value: '#6B7280', name: 'Cinza', class: 'bg-slate-500' },
  { value: '#78716C', name: 'Stone', class: 'bg-stone-500' },
];

// Opções de ícones para grupos - organizados por categoria
const DEPT_ICON_OPTIONS = [
  // Destaques / VibeDaY
  { value: 'sun', name: 'Sol', icon: Sun },
  { value: 'sparkles', name: 'Estrelas', icon: Sparkles },
  { value: 'star', name: 'Estrela', icon: Star },
  { value: 'zap', name: 'Energia', icon: Zap },
  { value: 'flame', name: 'Chama', icon: Flame },
  { value: 'rocket', name: 'Foguete', icon: Rocket },
  { value: 'gem', name: 'Diamante', icon: Gem },
  { value: 'crown', name: 'Coroa', icon: Crown },
  
  // Negócios
  { value: 'building', name: 'Prédio', icon: Building },
  { value: 'briefcase', name: 'Maleta', icon: Briefcase },
  { value: 'dollar', name: 'Financeiro', icon: DollarSign },
  { value: 'chart', name: 'Gráfico', icon: BarChart3 },
  { value: 'target', name: 'Alvo', icon: Target },
  { value: 'award', name: 'Prêmio', icon: Award },
  
  // Criativo / Mídia
  { value: 'palette', name: 'Design', icon: Palette },
  { value: 'camera', name: 'Câmera', icon: Camera },
  { value: 'video', name: 'Vídeo', icon: Video },
  { value: 'film', name: 'Filme', icon: Film },
  { value: 'music', name: 'Música', icon: Music },
  { value: 'mic', name: 'Microfone', icon: Mic },
  { value: 'headphones', name: 'Fones', icon: Headphones },
  { value: 'tv', name: 'TV', icon: Tv },
  { value: 'radio', name: 'Rádio', icon: Radio },
  
  // Tecnologia
  { value: 'cpu', name: 'CPU/TI', icon: Cpu },
  { value: 'lightbulb', name: 'Ideia', icon: Lightbulb },
  { value: 'gamepad', name: 'Games', icon: Gamepad2 },
  
  // Marketing / Comunicação
  { value: 'megaphone', name: 'Marketing', icon: Megaphone },
  { value: 'send', name: 'Envio', icon: Send },
  { value: 'mail', name: 'Email', icon: Mail },
  { value: 'phone', name: 'Telefone', icon: Phone },
  
  // Operações
  { value: 'wrench', name: 'Produção', icon: Wrench },
  { value: 'package', name: 'Pacote', icon: Package },
  { value: 'shield', name: 'Segurança', icon: Shield },
  { value: 'file', name: 'Documento', icon: FileText },
  
  // Pessoas / Social
  { value: 'users', name: 'Equipe', icon: Users },
  { value: 'heart', name: 'Coração', icon: Heart },
  { value: 'coffee', name: 'Café', icon: Coffee },
  { value: 'gift', name: 'Presente', icon: Gift },
  
  // Tempo / Agenda
  { value: 'calendar', name: 'Calendário', icon: Calendar },
  { value: 'moon', name: 'Lua', icon: Moon },
  { value: 'compass', name: 'Bússola', icon: Compass },
  
  // Viagem / Natureza
  { value: 'plane', name: 'Avião', icon: Plane },
  { value: 'car', name: 'Carro', icon: Car },
  { value: 'ship', name: 'Barco', icon: Ship },
  { value: 'mappin', name: 'Local', icon: MapPin },
  { value: 'mountain', name: 'Montanha', icon: Mountain },
  { value: 'waves', name: 'Ondas', icon: Waves },
  { value: 'tree', name: 'Árvore', icon: TreePine },
  { value: 'leaf', name: 'Folha', icon: Leaf },
  { value: 'flower', name: 'Flor', icon: Flower2 },
  { value: 'cloud', name: 'Nuvem', icon: Cloud },
  
  // Outros
  { value: 'home', name: 'Casa', icon: Home },
  { value: 'globe', name: 'Global', icon: Globe },
  { value: 'bookmark', name: 'Favorito', icon: Bookmark },
];

// Função para obter o componente de ícone pelo valor
const getIconComponent = (iconValue) => {
  const found = DEPT_ICON_OPTIONS.find(opt => opt.value === iconValue);
  return found?.icon || Building;
};

// Department colors and icons (legacy - fallback)
const DEPT_STYLES = {
  default: { color: 'slate', icon: Building },
  eventos: { color: 'purple', icon: Calendar },
  audiovisual: { color: 'blue', icon: Music },
  administrativo: { color: 'green', icon: DollarSign },
  marketing: { color: 'blue', icon: Megaphone },
  producao: { color: 'orange', icon: Wrench },
  design: { color: 'violet', icon: Palette },
  ti: { color: 'cyan', icon: Cpu },
  comercial: { color: 'amber', icon: Briefcase },
  financeiro: { color: 'emerald', icon: BarChart3 },
  juridico: { color: 'red', icon: FileText },
};

const getDeptStyle = (deptName, customColor = null, customIcon = null) => {
  // Se tem cor e ícone personalizados, usa eles
  if (customColor || customIcon) {
    return {
      color: customColor || '#6B7280',
      icon: customIcon ? getIconComponent(customIcon) : Building
    };
  }
  // Fallback para o sistema antigo baseado no nome
  const name = deptName?.toLowerCase() || '';
  for (const [key, style] of Object.entries(DEPT_STYLES)) {
    if (name.includes(key)) return style;
  }
  return DEPT_STYLES.default;
};

// Componente para cabeçalho de coluna ordenável
const SortableHeader = ({ column, children, sortColumn, sortDirection, onSort }) => {
  const isActive = sortColumn === column;
  return (
    <TableHead 
      className="dark:text-slate-300 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-700/50 select-none transition-colors"
      onClick={() => onSort(column)}
    >
      <div className="flex items-center gap-1">
        {children}
        {isActive ? (
          sortDirection === 'asc' ? (
            <ArrowUp className="w-3.5 h-3.5 text-blue-500" />
          ) : (
            <ArrowDown className="w-3.5 h-3.5 text-blue-500" />
          )
        ) : (
          <ArrowUpDown className="w-3.5 h-3.5 text-slate-400 opacity-0 group-hover:opacity-100" />
        )}
      </div>
    </TableHead>
  );
};

// Componente Sortable para Department Card
const SortableDepartmentCard = ({ dept, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: dept.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} className="flex flex-col group/dnd">
      <div className="relative">
        {/* Drag Handle - visível no hover */}
        <div 
          {...listeners}
          className="absolute -left-1 top-3 z-10 cursor-grab active:cursor-grabbing p-1 rounded hover:bg-slate-200/50 dark:hover:bg-slate-600/50 opacity-0 group-hover/dnd:opacity-100 transition-opacity"
          title="Arrastar para reordenar"
        >
          <GripVertical className="w-4 h-4 text-slate-400" />
        </div>
        {children}
      </div>
    </div>
  );
};

const ProjectsPage = () => {
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Project dialog
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [newProject, setNewProject] = useState({ name: '', description: '', department_id: '', sector_id: '', allowed_users: [], due_date: '' });
  const [creating, setCreating] = useState(false);
  const [availableSectors, setAvailableSectors] = useState([]);
  
  // Department dialog
  const [deptDialogOpen, setDeptDialogOpen] = useState(false);
  const [newDept, setNewDept] = useState({ name: '', description: '', color: '#F97316', icon: 'sun' });
  const [creatingDept, setCreatingDept] = useState(false);
  
  // Sector dialog
  const [sectorDialogOpen, setSectorDialogOpen] = useState(false);
  const [newSector, setNewSector] = useState({ name: '', description: '', department_id: '', start_date: '', end_date: '' });
  const [creatingSector, setCreatingSector] = useState(false);
  const [deletingSectorId, setDeletingSectorId] = useState(null);
  const [hiddenSectors, setHiddenSectors] = useState(() => {
    const saved = localStorage.getItem('vibeday_hidden_sectors');
    return saved ? JSON.parse(saved) : [];
  });
  const [showHiddenSectors, setShowHiddenSectors] = useState(false);
  const [sectorOrder, setSectorOrder] = useState(() => {
    const saved = localStorage.getItem('vibeday_sector_order');
    return saved ? JSON.parse(saved) : {};
  });
  
  // Edit Sector dialog
  const [editSectorDialogOpen, setEditSectorDialogOpen] = useState(false);
  const [editingSector, setEditingSector] = useState(null);
  const [savingSector, setSavingSector] = useState(false);
  
  // Edit Department dialog
  const [editDeptDialogOpen, setEditDeptDialogOpen] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [savingDept, setSavingDept] = useState(false);
  
  // Delete Department dialog
  const [deleteDeptDialogOpen, setDeleteDeptDialogOpen] = useState(false);
  const [deptToDelete, setDeptToDelete] = useState(null);
  const [deletingDept, setDeletingDept] = useState(false);

  // Permissions dialog
  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [savingPerms, setSavingPerms] = useState(false);
  
  // Edit project dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  
  // View mode and filters - Padrão é 'list' em vez de 'grid'
  const [viewMode, setViewMode] = useState('list'); // 'grid' or 'list'
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [sectorFilter, setSectorFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Timeline de Subgrupos
  const [timelineView, setTimelineView] = useState('month'); // 'month', 'quarter', 'year'
  const [timelineDate, setTimelineDate] = useState(new Date());
  const [showTimeline, setShowTimeline] = useState(true);
  const [cleaningOrphans, setCleaningOrphans] = useState(false);
  
  // Sorting
  const [sortColumn, setSortColumn] = useState('name'); // 'name', 'department', 'sector', 'created_at', 'due_date'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'

  // DND Kit sensors para drag-and-drop de grupos
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Requer arrastar 8px antes de ativar
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handler para quando o drag termina
  const handleDepartmentDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id && over) {
      const oldIndex = departments.findIndex((d) => d.id === active.id);
      const newIndex = departments.findIndex((d) => d.id === over.id);
      
      // Atualizar estado local imediatamente para feedback visual
      const newDepartments = arrayMove(departments, oldIndex, newIndex);
      setDepartments(newDepartments);
      
      // Salvar no backend
      try {
        await departmentApi.reorder(newDepartments.map(d => d.id));
        toast.success('Ordem dos grupos atualizada!');
      } catch (error) {
        console.error('Erro ao reordenar:', error);
        toast.error('Erro ao salvar a nova ordem');
        // Reverter se falhar
        loadData();
      }
    }
  };

  // Verifica se pode ver grupos (admin ou diretoria)
  const canViewDepartments = () => {
    const role = user?.role;
    return role === 'super_admin' || role === 'admin' || role === 'diretoria';
  };
  
  // Função para alternar ordenação
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };
  
  // Função para obter nome do grupo
  const getDepartmentName = (deptId) => {
    const dept = departments.find(d => d.id === deptId);
    return dept?.name || 'Sem grupo';
  };
  
  // Função para obter nome do subgrupo
  const getSectorName = (sectorId) => {
    if (!sectorId) return null;
    const sector = sectors.find(s => s.id === sectorId);
    return sector?.name || null;
  };
  
  // Função para ordenar projetos
  const sortedProjects = [...projects].sort((a, b) => {
    let valueA, valueB;
    
    switch (sortColumn) {
      case 'name':
        valueA = a.name?.toLowerCase() || '';
        valueB = b.name?.toLowerCase() || '';
        break;
      case 'department':
        valueA = getDepartmentName(a.department_id)?.toLowerCase() || '';
        valueB = getDepartmentName(b.department_id)?.toLowerCase() || '';
        break;
      case 'sector':
        valueA = (getSectorName(a.sector_id) || 'zzz')?.toLowerCase();
        valueB = (getSectorName(b.sector_id) || 'zzz')?.toLowerCase();
        break;
      case 'created_at':
        valueA = new Date(a.created_at || 0).getTime();
        valueB = new Date(b.created_at || 0).getTime();
        break;
      case 'due_date':
        valueA = a.due_date ? new Date(a.due_date).getTime() : Infinity;
        valueB = b.due_date ? new Date(b.due_date).getTime() : Infinity;
        break;
      default:
        valueA = a.name?.toLowerCase() || '';
        valueB = b.name?.toLowerCase() || '';
    }
    
    if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
    if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  useEffect(() => {
    loadData();
  }, []);
  
  // Carregar subgrupos quando grupo selecionado muda
  useEffect(() => {
    if (newProject.department_id) {
      loadSectorsForDepartment(newProject.department_id);
    } else {
      setAvailableSectors([]);
    }
  }, [newProject.department_id]);

  const loadData = async () => {
    try {
      const promises = [
        projectApi.listWithStats(),
        canViewDepartments() ? departmentApi.list() : Promise.resolve({ data: [] }),
        canViewDepartments() ? sectorApi.list() : Promise.resolve({ data: [] }),
        isAdmin() ? userApi.list() : Promise.resolve({ data: [] }),
      ];
      
      const [projectsRes, deptsRes, sectorsRes, usersRes] = await Promise.all(promises);
      setProjects(projectsRes.data);
      setDepartments(deptsRes.data);
      setSectors(sectorsRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      // Não mostrar erro se for apenas restrição de acesso a grupos
      if (!error.response?.status === 403) {
        toast.error('Erro ao carregar dados');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const loadSectorsForDepartment = async (deptId) => {
    try {
      const res = await departmentApi.listSectors(deptId);
      setAvailableSectors(res.data || []);
    } catch (error) {
      console.error('Error loading sectors:', error);
      setAvailableSectors([]);
    }
  };

  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      toast.error('Digite o nome do projeto');
      return;
    }
    if (!newProject.department_id) {
      toast.error('Selecione um grupo');
      return;
    }

    setCreating(true);
    try {
      await projectApi.create(newProject);
      toast.success('Projeto criado com sucesso!');
      setNewProject({ name: '', description: '', department_id: '', sector_id: '', allowed_users: [], due_date: '' });
      setAvailableSectors([]);
      setProjectDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao criar projeto');
    } finally {
      setCreating(false);
    }
  };
  
  const handleCreateSector = async () => {
    if (!newSector.name.trim()) {
      toast.error('Digite o nome do subgrupo');
      return;
    }
    if (!newSector.department_id) {
      toast.error('Selecione um grupo');
      return;
    }

    setCreatingSector(true);
    try {
      await sectorApi.create(newSector);
      toast.success('Subgrupo criado com sucesso!');
      // Mantém o department_id para facilitar criar múltiplos subgrupos
      const deptId = newSector.department_id;
      setNewSector({ name: '', description: '', department_id: deptId, start_date: '', end_date: '' });
      loadData();
      // NÃO fecha o dialog para permitir criar múltiplos subgrupos
    } catch (error) {
      toast.error('Erro ao criar subgrupo');
    } finally {
      setCreatingSector(false);
    }
  };

  const handleDeleteSector = async (sectorId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    if (!window.confirm('Tem certeza que deseja excluir este subgrupo?')) return;
    
    setDeletingSectorId(sectorId);
    try {
      await sectorApi.delete(sectorId);
      toast.success('Subgrupo excluído!');
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao excluir subgrupo';
      toast.error(message);
    } finally {
      setDeletingSectorId(null);
    }
  };

  // Funções para editar subgrupo
  const openEditSectorDialog = (sector, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingSector({ ...sector });
    setEditSectorDialogOpen(true);
  };

  const handleSaveSector = async () => {
    if (!editingSector?.name?.trim()) {
      toast.error('Digite o nome do subgrupo');
      return;
    }
    
    setSavingSector(true);
    try {
      await sectorApi.update(editingSector.id, {
        name: editingSector.name.trim(),
        description: (editingSector.description || '').trim(),
        start_date: editingSector.start_date || null,
        end_date: editingSector.end_date || null
      });
      toast.success('Subgrupo atualizado!');
      setEditSectorDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar subgrupo');
    } finally {
      setSavingSector(false);
    }
  };

  // Função para limpar subgrupos órfãos
  const handleCleanupOrphans = async () => {
    if (!window.confirm('Isso removerá subgrupos que não pertencem a nenhum grupo existente. Continuar?')) return;
    
    setCleaningOrphans(true);
    try {
      const res = await sectorApi.cleanupOrphans();
      toast.success(res.data.message || 'Limpeza concluída');
      loadData();
    } catch (error) {
      toast.error('Erro ao limpar subgrupos órfãos');
    } finally {
      setCleaningOrphans(false);
    }
  };

  // Funções para ocultar/exibir subgrupos
  const toggleSectorVisibility = (sectorId) => {
    setHiddenSectors(prev => {
      const newHidden = prev.includes(sectorId) 
        ? prev.filter(id => id !== sectorId)
        : [...prev, sectorId];
      localStorage.setItem('vibeday_hidden_sectors', JSON.stringify(newHidden));
      return newHidden;
    });
  };

  // Funções para reordenar subgrupos
  const moveSector = (deptId, sectorId, direction) => {
    const deptSectors = sectors
      .filter(s => s.department_id === deptId)
      .sort((a, b) => {
        const orderA = sectorOrder[a.id] ?? 999;
        const orderB = sectorOrder[b.id] ?? 999;
        return orderA - orderB;
      });
    
    const currentIndex = deptSectors.findIndex(s => s.id === sectorId);
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= deptSectors.length) return;
    
    const newOrder = { ...sectorOrder };
    deptSectors.forEach((s, idx) => {
      if (idx === currentIndex) {
        newOrder[s.id] = newIndex;
      } else if (idx === newIndex) {
        newOrder[s.id] = currentIndex;
      } else {
        newOrder[s.id] = idx;
      }
    });
    
    setSectorOrder(newOrder);
    localStorage.setItem('vibeday_sector_order', JSON.stringify(newOrder));
  };

  // Ordenar subgrupos de um grupo
  const getSortedSectors = (deptId) => {
    return sectors
      .filter(s => s.department_id === deptId)
      .sort((a, b) => {
        const orderA = sectorOrder[a.id] ?? 999;
        const orderB = sectorOrder[b.id] ?? 999;
        return orderA - orderB;
      });
  };

  const openPermissionsDialog = (project, e) => {
    e.stopPropagation();
    setSelectedProject(project);
    setSelectedUsers(project.allowed_users || []);
    setPermDialogOpen(true);
  };
  
  const openEditDialog = (project, e) => {
    if (e) e.stopPropagation();
    setEditingProject({
      id: project.id,
      name: project.name,
      description: project.description || '',
      department_id: project.department_id || '',
      sector_id: project.sector_id || ''
    });
    // Carregar subgrupos do grupo
    if (project.department_id) {
      loadSectorsForDepartment(project.department_id);
    }
    setEditDialogOpen(true);
  };
  
  const handleSaveEdit = async () => {
    if (!editingProject.name.trim()) {
      toast.error('Digite o nome do projeto');
      return;
    }
    
    setSavingEdit(true);
    try {
      await projectApi.update(editingProject.id, {
        name: editingProject.name,
        description: editingProject.description,
        department_id: editingProject.department_id,
        sector_id: editingProject.sector_id || null
      });
      toast.success('Projeto atualizado!');
      setEditDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar projeto');
    } finally {
      setSavingEdit(false);
    }
  };

  const toggleUserPermission = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSavePermissions = async () => {
    setSavingPerms(true);
    try {
      await projectApi.updatePermissions(selectedProject.id, selectedUsers);
      toast.success('Permissões atualizadas!');
      setPermDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao salvar permissões');
    } finally {
      setSavingPerms(false);
    }
  };

  const toggleNewProjectUser = (userId) => {
    setNewProject(prev => ({
      ...prev,
      allowed_users: prev.allowed_users.includes(userId)
        ? prev.allowed_users.filter(id => id !== userId)
        : [...prev.allowed_users, userId]
    }));
  };

  const handleCreateDepartment = async () => {
    if (!newDept.name.trim()) {
      toast.error('Digite o nome do grupo');
      return;
    }

    setCreatingDept(true);
    try {
      await departmentApi.create(newDept);
      toast.success('Grupo criado!');
      setNewDept({ name: '', description: '' });
      setDeptDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao criar grupo');
    } finally {
      setCreatingDept(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Tem certeza que deseja excluir este projeto?')) return;
    
    try {
      await projectApi.delete(projectId);
      toast.success('Projeto excluído');
      loadData();
    } catch (error) {
      toast.error('Erro ao excluir projeto');
    }
  };

  const handleUpdateProjectFlag = async (projectId, flag, e) => {
    if (e) e.stopPropagation();
    try {
      await flagsApi.updateProjectFlag(projectId, flag);
      const flagLabels = {
        'proxima_acao': 'Próxima Ação',
        'aguardando_retorno': 'Aguardando Retorno',
        'revisar': 'Revisar'
      };
      toast.success(flag ? `Flag definida: ${flagLabels[flag]}` : 'Flag removida');
      loadData();
    } catch (error) {
      console.error('Error updating flag:', error);
      toast.error('Erro ao atualizar flag');
    }
  };

  const getFlagInfo = (flag) => {
    const flags = {
      'proxima_acao': { label: 'Próxima Ação', color: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-700', icon: PlayCircle },
      'aguardando_retorno': { label: 'Aguardando', color: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-700', icon: Clock },
      'revisar': { label: 'Revisar', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-700', icon: Eye },
    };
    return flags[flag] || null;
  };

  const handleDeleteDepartment = async (deptId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    
    // Find department info for modal
    const dept = departments.find(d => d.id === deptId);
    setDeptToDelete(dept);
    setDeleteDeptDialogOpen(true);
  };
  
  const confirmDeleteDepartment = async () => {
    if (!deptToDelete) return;
    
    setDeletingDept(true);
    try {
      await departmentApi.delete(deptToDelete.id);
      toast.success('Grupo excluído!');
      setDeleteDeptDialogOpen(false);
      setDeptToDelete(null);
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erro ao excluir grupo';
      toast.error(message);
    } finally {
      setDeletingDept(false);
    }
  };
  
  const openEditDeptDialog = (dept, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setEditingDept({
      id: dept.id,
      name: dept.name,
      description: dept.description || ''
    });
    setEditDeptDialogOpen(true);
  };
  
  const handleSaveDept = async () => {
    if (!editingDept.name.trim()) {
      toast.error('Digite o nome do grupo');
      return;
    }
    
    setSavingDept(true);
    try {
      await departmentApi.update(editingDept.id, {
        name: editingDept.name.trim(),
        description: (editingDept.description || '').trim(),
        color: editingDept.color,
        icon: editingDept.icon
      });
      toast.success('Grupo atualizado!');
      setEditDeptDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar grupo');
    } finally {
      setSavingDept(false);
    }
  };

  // Filter projects based on department, sector and search term
  const filteredProjects = sortedProjects.filter(project => {
    // Filter by department
    if (departmentFilter !== 'all' && project.department_id !== departmentFilter) {
      return false;
    }
    // Filter by sector
    if (sectorFilter !== 'all' && project.sector_id !== sectorFilter) {
      return false;
    }
    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return project.name.toLowerCase().includes(search) || 
             project.description?.toLowerCase().includes(search);
    }
    return true;
  });
  
  // Subgrupos filtrados pelo grupo selecionado
  const filteredSectors = departmentFilter !== 'all' 
    ? sectors.filter(s => s.department_id === departmentFilter)
    : sectors;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="projects-page" className="space-y-8 animate-fade-in">
      {/* Grupos e Subgrupos */}
      {canViewDepartments() && (
        <Card className="dark:bg-slate-800/50 dark:border-slate-700">
          <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
              <Layers className="w-5 h-5 text-violet-500" />
              Grupos e Subgrupos
              <Badge variant="secondary" className="ml-2">
                {departments.length} dept{departments.length !== 1 ? 's' : ''} • {sectors.length} subgrupo{sectors.length !== 1 ? 'es' : ''}
              </Badge>
            </CardTitle>
            <div className="flex items-center gap-2">
              {/* Toggle para mostrar subgrupos minimizados */}
              {hiddenSectors.length > 0 && (
                <Button
                  variant={showHiddenSectors ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowHiddenSectors(!showHiddenSectors)}
                  className={showHiddenSectors ? "" : "text-slate-500 dark:text-slate-400"}
                >
                  {showHiddenSectors ? <Eye className="w-4 h-4 mr-1" /> : <EyeOff className="w-4 h-4 mr-1" />}
                  {showHiddenSectors ? 'Ocultar minimizados' : `${hiddenSectors.length} minimizado${hiddenSectors.length !== 1 ? 's' : ''}`}
                </Button>
              )}
              {isAdmin() && (
                <>
                  <Dialog open={deptDialogOpen} onOpenChange={setDeptDialogOpen}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700">
                        <Plus className="w-4 h-4 mr-1" />
                        Grupo
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="dark:bg-slate-800 dark:border-slate-700 sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle className="dark:text-slate-100">Novo Grupo</DialogTitle>
                        <DialogDescription className="dark:text-slate-400">
                          Crie um novo grupo para organizar projetos
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label htmlFor="dept-name" className="dark:text-slate-300">Nome do Grupo</Label>
                          <Input
                            id="dept-name"
                            data-testid="dept-name-input"
                            value={newDept.name}
                            onChange={(e) => setNewDept({ ...newDept, name: e.target.value })}
                            placeholder="Ex: Comercial"
                            className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="dept-desc" className="dark:text-slate-300">Descrição (opcional)</Label>
                          <Textarea
                            id="dept-desc"
                            data-testid="dept-desc-input"
                            value={newDept.description}
                            onChange={(e) => setNewDept({ ...newDept, description: e.target.value })}
                            placeholder="Descrição do grupo..."
                            className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                          />
                        </div>
                        {/* Seletor de Cor */}
                        <div className="space-y-2">
                          <Label className="dark:text-slate-300">Cor</Label>
                          <div className="flex flex-wrap gap-2">
                            {DEPT_COLOR_OPTIONS.map((colorOpt) => (
                              <button
                                key={colorOpt.value}
                                type="button"
                                className={`w-8 h-8 rounded-full border-2 transition-all ${
                                  newDept.color === colorOpt.value 
                                    ? 'border-slate-900 dark:border-white scale-110' 
                                    : 'border-transparent hover:scale-105'
                                }`}
                                style={{ backgroundColor: colorOpt.value }}
                                onClick={() => setNewDept({ ...newDept, color: colorOpt.value })}
                                title={colorOpt.name}
                              />
                            ))}
                          </div>
                        </div>
                        {/* Seletor de Ícone */}
                        <div className="space-y-2">
                          <Label className="dark:text-slate-300">Ícone</Label>
                          <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border rounded-md dark:border-slate-600">
                            {DEPT_ICON_OPTIONS.map((iconOpt) => {
                              const IconComp = iconOpt.icon;
                              return (
                                <button
                                  key={iconOpt.value}
                                  type="button"
                                  className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-all ${
                                    newDept.icon === iconOpt.value 
                                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600' 
                                      : 'border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400'
                                  }`}
                                  onClick={() => setNewDept({ ...newDept, icon: iconOpt.value })}
                                  title={iconOpt.name}
                                >
                                  <IconComp className="w-4 h-4" />
                                </button>
                              );
                            })}
                          </div>
                        </div>
                        {/* Preview */}
                        <div className="space-y-2">
                          <Label className="dark:text-slate-300">Prévia</Label>
                          <div 
                            className="flex items-center gap-2 p-3 rounded-lg border"
                            style={{ borderColor: newDept.color, backgroundColor: `${newDept.color}10` }}
                          >
                            {(() => {
                              const IconPreview = getIconComponent(newDept.icon);
                              return <IconPreview className="w-5 h-5" style={{ color: newDept.color }} />;
                            })()}
                            <span className="font-medium" style={{ color: newDept.color }}>
                              {newDept.name || 'Nome do Grupo'}
                            </span>
                          </div>
                        </div>
                      </div>
                    <DialogFooter>
                      <Button variant="outline" onClick={() => setDeptDialogOpen(false)} className="dark:border-slate-600 dark:text-slate-300">
                        Cancelar
                      </Button>
                      <Button
                        data-testid="create-dept-submit"
                        onClick={handleCreateDepartment}
                        disabled={creatingDept}
                      >
                        {creatingDept && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        Criar
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                  onClick={() => setSectorDialogOpen(true)}
                  disabled={departments.length === 0}
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Subgrupo
                </Button>
                {/* Botão para limpar subgrupos órfãos - só aparece se houver subgrupos sem grupo */}
                {sectors.some(s => !departments.find(d => d.id === s.department_id)) && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="text-red-400 hover:text-red-500 hover:bg-red-500/10"
                    onClick={handleCleanupOrphans}
                    disabled={cleaningOrphans}
                    title="Remover subgrupos órfãos"
                  >
                    {cleaningOrphans ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  </Button>
                )}
                </>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {departments.length === 0 ? (
              <div className="text-center py-8">
                <Layers className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-600 mb-3" />
                <p className="text-slate-500 dark:text-slate-400">Nenhum grupo criado</p>
                <p className="text-sm text-slate-400 dark:text-slate-500 mt-1">Crie grupos para organizar seus projetos</p>
              </div>
            ) : (
              <div className="relative">
                {/* Ícone central VibeDaY - estilo da tela inicial (laranja com sol) */}
                <div className="flex flex-col items-center mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-400 via-orange-500 to-amber-500 flex items-center justify-center shadow-lg relative overflow-hidden">
                    {/* Efeito de brilho */}
                    <div className="absolute inset-0 bg-gradient-to-tr from-white/20 to-transparent" />
                    {/* Ícone de sol */}
                    <Sun className="w-7 h-7 text-white drop-shadow-sm relative z-10" />
                    {/* Estrelinhas decorativas */}
                    <Sparkles className="w-3 h-3 text-yellow-200 absolute top-1 right-1 opacity-80" />
                  </div>
                  {departments.length > 0 && (
                    <div className="w-0.5 h-6 bg-gradient-to-b from-violet-500 to-slate-300 dark:to-slate-600 mt-2" />
                  )}
                </div>

                {/* Grupos em Grid Compacto com Drag-and-Drop */}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDepartmentDragEnd}
                >
                  <SortableContext
                    items={departments.map(d => d.id)}
                    strategy={rectSortingStrategy}
                  >
                    <div className={`grid gap-4 ${
                      departments.length === 1 ? 'grid-cols-1 max-w-sm mx-auto' :
                      departments.length === 2 ? 'grid-cols-2 max-w-xl mx-auto' :
                      'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'
                    }`}>
                    {departments.map((dept) => {
                      // Prioriza cor/ícone personalizado, senão usa fallback baseado no nome
                      const hasCustomStyle = dept.color || dept.icon;
                      const IconComponent = dept.icon ? getIconComponent(dept.icon) : getDeptStyle(dept.name).icon;
                      const customColor = dept.color;
                      const deptSectors = getSortedSectors(dept.id);
                      const visibleSectors = showHiddenSectors ? deptSectors : deptSectors.filter(s => !hiddenSectors.includes(s.id));
                      const hiddenCount = deptSectors.filter(s => hiddenSectors.includes(s.id)).length;
                      
                      // Se tem cor personalizada, usa estilo inline. Senão, usa classes CSS
                      const style = getDeptStyle(dept.name);
                      const colorClasses = {
                        slate: { bg: 'bg-slate-50 dark:bg-slate-800/80', border: 'border-slate-200 dark:border-slate-700', text: 'text-slate-600 dark:text-slate-300', iconBg: 'bg-slate-200 dark:bg-slate-600' },
                        purple: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-600 dark:text-blue-400', iconBg: 'bg-blue-100 dark:bg-blue-900/40' },
                        blue: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-600 dark:text-blue-400', iconBg: 'bg-blue-100 dark:bg-blue-900/40' },
                        green: { bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-200 dark:border-green-800', text: 'text-green-600 dark:text-green-400', iconBg: 'bg-green-100 dark:bg-green-900/40' },
                        pink: { bg: 'bg-blue-50 dark:bg-blue-900/20', border: 'border-blue-200 dark:border-blue-800', text: 'text-blue-600 dark:text-blue-400', iconBg: 'bg-blue-100 dark:bg-blue-900/40' },
                        orange: { bg: 'bg-orange-50 dark:bg-orange-900/20', border: 'border-orange-200 dark:border-orange-800', text: 'text-orange-600 dark:text-orange-400', iconBg: 'bg-orange-100 dark:bg-orange-900/40' },
                        violet: { bg: 'bg-violet-50 dark:bg-violet-900/20', border: 'border-violet-200 dark:border-violet-800', text: 'text-violet-600 dark:text-violet-400', iconBg: 'bg-violet-100 dark:bg-violet-900/40' },
                        cyan: { bg: 'bg-cyan-50 dark:bg-cyan-900/20', border: 'border-cyan-200 dark:border-cyan-800', text: 'text-cyan-600 dark:text-cyan-400', iconBg: 'bg-cyan-100 dark:bg-cyan-900/40' },
                        amber: { bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-200 dark:border-amber-800', text: 'text-amber-600 dark:text-amber-400', iconBg: 'bg-amber-100 dark:bg-amber-900/40' },
                        emerald: { bg: 'bg-emerald-50 dark:bg-emerald-900/20', border: 'border-emerald-200 dark:border-emerald-800', text: 'text-emerald-600 dark:text-emerald-400', iconBg: 'bg-emerald-100 dark:bg-emerald-900/40' },
                        red: { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-200 dark:border-red-800', text: 'text-red-600 dark:text-red-400', iconBg: 'bg-red-100 dark:bg-red-900/40' },
                      };
                      
                      const colors = colorClasses[style.color] || colorClasses.slate;
                      
                      return (
                        <SortableDepartmentCard key={dept.id} dept={dept}>
                          {/* Card do Grupo - Compacto */}
                          <div 
                            className={`relative w-full p-3 pl-6 rounded-lg border transition-all hover:shadow-md group ${!customColor ? `${colors.bg} ${colors.border}` : ''}`}
                            style={customColor ? { 
                              backgroundColor: `${customColor}10`, 
                              borderColor: `${customColor}40`,
                              borderWidth: '1px'
                            } : {}}
                          >
                            {/* Header do grupo */}
                            <div className="flex items-center gap-2 mb-2">
                              {/* Ícone estilizado como na tela inicial */}
                              <div 
                                className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
                                style={{ 
                                  background: customColor 
                                    ? `linear-gradient(135deg, ${customColor}, ${customColor}dd)` 
                                    : `linear-gradient(135deg, var(--dept-color, #8B5CF6), var(--dept-color-dark, #7C3AED))`
                                }}
                              >
                                <IconComponent 
                                  className="w-4.5 h-4.5 text-white drop-shadow-sm"
                                  style={{ width: '18px', height: '18px' }}
                                />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h3 
                                  className={`font-semibold text-sm truncate ${!customColor ? colors.text : ''}`}
                                  style={customColor ? { color: customColor } : {}}
                                >
                                  {dept.name}
                                </h3>
                              </div>
                              {isAdmin() && (
                                <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-slate-400 hover:text-blue-600"
                                    onClick={(e) => openEditDeptDialog(dept, e)}
                                  >
                                    <Pencil className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-6 w-6 text-slate-400 hover:text-red-500"
                                    onClick={(e) => handleDeleteDepartment(dept.id, e)}
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              )}
                            </div>
                            
                            {/* Subgrupos do grupo - ScrollArea para muitos */}
                            {deptSectors.length > 0 && (
                              <div className="border-t border-slate-200 dark:border-slate-600 pt-2">
                                <div className="flex items-center justify-between mb-1.5">
                                  <span className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">
                                    Subgrupos ({visibleSectors.length}{hiddenCount > 0 ? `/${deptSectors.length}` : ''})
                                  </span>
                                  {isAdmin() && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="h-5 px-1.5 text-[10px] text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30"
                                      onClick={() => {
                                        setNewSector({ ...newSector, department_id: dept.id });
                                        setSectorDialogOpen(true);
                                      }}
                                    >
                                      <Plus className="w-3 h-3" />
                                    </Button>
                                  )}
                                </div>
                                <ScrollArea className={deptSectors.length > 5 ? 'h-32' : ''}>
                                  <div className="space-y-1">
                                    {visibleSectors.map((sector, idx) => {
                                      const isHidden = hiddenSectors.includes(sector.id);
                                      return (
                                        <div 
                                          key={sector.id}
                                          className={`flex items-center gap-1.5 py-1 px-1.5 rounded text-xs group/sector ${
                                            isHidden ? 'opacity-60 bg-slate-100 dark:bg-slate-700' : 'hover:bg-white/50 dark:hover:bg-slate-700/50'
                                          }`}
                                        >
                                          {isAdmin() && (
                                            <div className="flex flex-col opacity-0 group-hover/sector:opacity-100 transition-opacity flex-shrink-0">
                                              <button
                                                onClick={() => moveSector(dept.id, sector.id, 'up')}
                                                disabled={idx === 0}
                                                className="text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                              >
                                                <ChevronUp className="w-3 h-3" />
                                              </button>
                                              <button
                                                onClick={() => moveSector(dept.id, sector.id, 'down')}
                                                disabled={idx === visibleSectors.length - 1}
                                                className="text-slate-400 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                              >
                                                <ChevronDown className="w-3 h-3" />
                                              </button>
                                            </div>
                                          )}
                                          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                            isHidden ? 'bg-slate-400' : 'bg-blue-500'
                                          }`} />
                                          <TooltipProvider delayDuration={300}>
                                            <Tooltip>
                                              <TooltipTrigger asChild>
                                                <span 
                                                  className={`flex-1 min-w-0 truncate cursor-pointer ${
                                                    isHidden ? 'text-slate-400 italic' : 'text-slate-700 dark:text-slate-300'
                                                  }`}
                                                  onClick={(e) => isAdmin() && openEditSectorDialog(sector, e)}
                                                >
                                                  {sector.name}
                                                </span>
                                              </TooltipTrigger>
                                              <TooltipContent 
                                                side="top" 
                                                className="max-w-xs bg-slate-900 dark:bg-slate-700 text-white px-3 py-2 rounded-lg shadow-lg"
                                              >
                                                <p className="font-semibold text-sm">{sector.name}</p>
                                                {sector.description && (
                                                  <p className="text-xs text-slate-300 mt-1">{sector.description}</p>
                                                )}
                                                {(sector.start_date || sector.end_date) && (
                                                  <p className="text-xs text-slate-400 mt-1">
                                                    {sector.start_date && `Início: ${new Date(sector.start_date).toLocaleDateString('pt-BR')}`}
                                                    {sector.start_date && sector.end_date && ' • '}
                                                    {sector.end_date && `Término: ${new Date(sector.end_date).toLocaleDateString('pt-BR')}`}
                                                  </p>
                                                )}
                                                {!sector.description && !sector.start_date && !sector.end_date && (
                                                  <p className="text-xs text-slate-400 italic">Sem descrição</p>
                                                )}
                                              </TooltipContent>
                                            </Tooltip>
                                          </TooltipProvider>
                                          {isAdmin() && (
                                            <div className="flex gap-0.5 flex-shrink-0 ml-1">
                                              <button
                                                onClick={(e) => openEditSectorDialog(sector, e)}
                                                className="text-slate-400 hover:text-blue-500 p-0.5"
                                                title="Editar subgrupo"
                                              >
                                                <Pencil className="w-3 h-3" />
                                              </button>
                                              <button
                                                onClick={() => toggleSectorVisibility(sector.id)}
                                                className={`p-0.5 ${isHidden ? 'text-green-500 hover:text-green-600' : 'text-slate-400 hover:text-amber-500'}`}
                                                title={isHidden ? 'Exibir subgrupo' : 'Minimizar subgrupo'}
                                              >
                                                {isHidden ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                                              </button>
                                              <button
                                                onClick={(e) => handleDeleteSector(sector.id, e)}
                                                disabled={deletingSectorId === sector.id}
                                                className="text-slate-400 hover:text-red-500 p-0.5"
                                              >
                                                {deletingSectorId === sector.id ? (
                                                  <Loader2 className="w-3 h-3 animate-spin" />
                                                ) : (
                                                  <Trash2 className="w-3 h-3" />
                                                )}
                                              </button>
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })}
                                    {/* Botão para mostrar subgrupos ocultos deste grupo */}
                                    {!showHiddenSectors && hiddenCount > 0 && (
                                      <button
                                        onClick={() => setShowHiddenSectors(true)}
                                        className="w-full flex items-center justify-center gap-1 py-1.5 px-2 mt-1 rounded text-[10px] bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                                      >
                                        <Eye className="w-3 h-3" />
                                        Mostrar {hiddenCount} subgrupo{hiddenCount !== 1 ? 'es' : ''} minimizado{hiddenCount !== 1 ? 's' : ''}
                                      </button>
                                    )}
                                  </div>
                                </ScrollArea>
                              </div>
                            )}
                            
                            {/* Botão para adicionar subgrupo quando não há nenhum */}
                            {isAdmin() && deptSectors.length === 0 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="w-full mt-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 border border-dashed border-slate-300 dark:border-slate-600"
                                onClick={() => {
                                  setNewSector({ ...newSector, department_id: dept.id });
                                  setSectorDialogOpen(true);
                                }}
                              >
                                <Plus className="w-3 h-3 mr-1" />
                                Adicionar Subgrupo
                              </Button>
                            )}
                          </div>
                        </SortableDepartmentCard>
                      );
                    })}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      
      {/* Dialog de criação de subgrupo (para ser usado por qualquer botão) */}
      <Dialog open={sectorDialogOpen} onOpenChange={setSectorDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">Criar Subgrupo</DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              Adicione um novo subgrupo (subgrupo) a um grupo
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="dark:text-slate-300">Grupo</Label>
              <Select
                value={newSector.department_id}
                onValueChange={(value) => setNewSector({ ...newSector, department_id: value })}
              >
                <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                  <SelectValue placeholder="Selecione o grupo" />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id} className="dark:text-slate-200 dark:focus:bg-slate-700">
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="dark:text-slate-300">Nome do Subgrupo</Label>
              <Input
                value={newSector.name}
                onChange={(e) => setNewSector({ ...newSector, name: e.target.value })}
                placeholder="Ex: Digestivo"
                className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
              />
            </div>
            <div className="space-y-2">
              <Label className="dark:text-slate-300">Descrição (opcional)</Label>
              <Textarea
                value={newSector.description}
                onChange={(e) => setNewSector({ ...newSector, description: e.target.value })}
                placeholder="Descrição do subgrupo..."
                className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
              />
            </div>
            {/* Datas de início e término (apenas admin) */}
            {isAdmin() && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="dark:text-slate-300 flex items-center gap-1">
                    <CalendarDays className="w-3.5 h-3.5" />
                    Data de Início
                  </Label>
                  <Input
                    type="date"
                    value={newSector.start_date}
                    onChange={(e) => setNewSector({ ...newSector, start_date: e.target.value })}
                    className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="dark:text-slate-300 flex items-center gap-1">
                    <CalendarDays className="w-3.5 h-3.5" />
                    Data de Término
                  </Label>
                  <Input
                    type="date"
                    value={newSector.end_date}
                    onChange={(e) => setNewSector({ ...newSector, end_date: e.target.value })}
                    className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                  />
                </div>
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button 
              variant="outline" 
              onClick={() => {
                setNewSector({ name: '', description: '', department_id: '', start_date: '', end_date: '' });
                setSectorDialogOpen(false);
              }} 
              className="dark:border-slate-600 dark:text-slate-300"
            >
              Concluir
            </Button>
            <Button onClick={handleCreateSector} disabled={creatingSector || !newSector.name.trim() || !newSector.department_id}>
              {creatingSector && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              <Plus className="w-4 h-4 mr-1" />
              Adicionar Subgrupo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Timeline de Subgrupos - apenas admin */}
      {isAdmin() && (
        <Card className="mb-6 dark:bg-slate-800/50 dark:border-slate-700 overflow-hidden">
          <CardHeader className="pb-2">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <CardTitle className="text-lg flex items-center gap-2 dark:text-slate-100">
                <Layers className="w-5 h-5 text-cyan-500" />
                Timeline de Subgrupos
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowTimeline(!showTimeline)}
                  className="ml-2 h-6 w-6 p-0"
                >
                  {showTimeline ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              </CardTitle>
              
              {showTimeline && (
                <div className="flex items-center gap-2">
                  {/* Navegação de período */}
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 dark:border-slate-600"
                    onClick={() => {
                      if (timelineView === 'month') setTimelineDate(subMonths(timelineDate, 1));
                      else if (timelineView === 'quarter') setTimelineDate(subQuarters(timelineDate, 1));
                      else setTimelineDate(subYears(timelineDate, 1));
                    }}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  
                  <span className="text-sm font-medium dark:text-slate-300 min-w-[120px] text-center">
                    {timelineView === 'month' && format(timelineDate, 'MMMM yyyy', { locale: ptBR })}
                    {timelineView === 'quarter' && `${Math.ceil((timelineDate.getMonth() + 1) / 3)}º Tri ${timelineDate.getFullYear()}`}
                    {timelineView === 'year' && timelineDate.getFullYear()}
                  </span>
                  
                  <Button
                    variant="outline"
                    size="icon"
                    className="h-8 w-8 dark:border-slate-600"
                    onClick={() => {
                      if (timelineView === 'month') setTimelineDate(addMonths(timelineDate, 1));
                      else if (timelineView === 'quarter') setTimelineDate(addQuarters(timelineDate, 1));
                      else setTimelineDate(addYears(timelineDate, 1));
                    }}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  
                  {/* Seletor de visualização */}
                  <Select value={timelineView} onValueChange={setTimelineView}>
                    <SelectTrigger className="w-32 h-8 dark:bg-slate-900 dark:border-slate-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                      <SelectItem value="month" className="dark:text-slate-200">Por Mês</SelectItem>
                      <SelectItem value="quarter" className="dark:text-slate-200">Por Trimestre</SelectItem>
                      <SelectItem value="year" className="dark:text-slate-200">Por Ano</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardHeader>
          
          {showTimeline && (
            <CardContent className="pt-2">
              {(() => {
                // Calcular período baseado na visualização
                let periodStart, periodEnd, divisions, divisionLabels;
                
                if (timelineView === 'month') {
                  periodStart = startOfMonth(timelineDate);
                  periodEnd = endOfMonth(timelineDate);
                  // Dividir em semanas
                  const weeks = eachWeekOfInterval({ start: periodStart, end: periodEnd }, { weekStartsOn: 0 });
                  divisions = weeks.length;
                  divisionLabels = weeks.map((w, i) => `S${i + 1}`);
                } else if (timelineView === 'quarter') {
                  periodStart = startOfQuarter(timelineDate);
                  periodEnd = endOfQuarter(timelineDate);
                  // Dividir em 3 meses
                  const months = eachMonthOfInterval({ start: periodStart, end: periodEnd });
                  divisions = months.length;
                  divisionLabels = months.map(m => format(m, 'MMM', { locale: ptBR }));
                } else {
                  periodStart = startOfYear(timelineDate);
                  periodEnd = endOfYear(timelineDate);
                  // Dividir em 12 meses
                  const months = eachMonthOfInterval({ start: periodStart, end: periodEnd });
                  divisions = months.length;
                  divisionLabels = months.map(m => format(m, 'MMM', { locale: ptBR }));
                }
                
                const totalDays = differenceInDays(periodEnd, periodStart) || 1;
                
                // Filtrar subgrupos que têm datas
                const sectorsWithDates = sectors.filter(s => s.start_date || s.end_date);
                
                // Calcular posição de cada subgrupo
                const getSectorBar = (sector) => {
                  const startDate = sector.start_date ? parseISO(sector.start_date) : periodStart;
                  const endDate = sector.end_date ? parseISO(sector.end_date) : periodEnd;
                  
                  // Se não está no período, retorna null
                  if (endDate < periodStart || startDate > periodEnd) return null;
                  
                  const effectiveStart = startDate < periodStart ? periodStart : startDate;
                  const effectiveEnd = endDate > periodEnd ? periodEnd : endDate;
                  
                  const startDay = differenceInDays(effectiveStart, periodStart);
                  const endDay = differenceInDays(effectiveEnd, periodStart);
                  
                  const left = (startDay / totalDays) * 100;
                  const width = Math.max(((endDay - startDay + 1) / totalDays) * 100, 3);
                  
                  const now = new Date();
                  const isFuture = startDate > now;
                  const isActive = now >= startDate && now <= endDate;
                  const isPast = endDate < now;
                  
                  return { left, width, isFuture, isActive, isPast, startDate, endDate };
                };
                
                const dept = (deptId) => departments.find(d => d.id === deptId);
                
                return (
                  <div className="space-y-2">
                    {/* Header com divisões */}
                    <div className="flex border-b border-slate-200 dark:border-slate-700 pb-1">
                      <div className="w-32 flex-shrink-0 text-xs font-medium text-slate-500 dark:text-slate-400">
                        Subgrupo
                      </div>
                      <div className="flex-1 flex">
                        {divisionLabels.map((label, idx) => (
                          <div 
                            key={idx} 
                            className="flex-1 text-center text-[10px] font-medium text-slate-400 dark:text-slate-500 uppercase"
                          >
                            {label}
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Linhas de subgrupos */}
                    {sectorsWithDates.length === 0 ? (
                      <div className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">
                        <Layers className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        Nenhum subgrupo com datas neste período
                      </div>
                    ) : (
                      sectorsWithDates.map(sector => {
                        const bar = getSectorBar(sector);
                        if (!bar) return null;
                        
                        const deptInfo = dept(sector.department_id);
                        
                        return (
                          <div key={sector.id} className="flex items-center h-8 group">
                            {/* Nome do subgrupo */}
                            <TooltipProvider delayDuration={200}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <div className="w-32 flex-shrink-0 pr-2 truncate text-xs font-medium text-slate-700 dark:text-slate-300 cursor-pointer">
                                    {sector.name}
                                  </div>
                                </TooltipTrigger>
                                <TooltipContent side="right" className="max-w-xs bg-slate-900 dark:bg-slate-700 text-white px-3 py-2">
                                  <p className="font-semibold">{sector.name}</p>
                                  <p className="text-xs text-slate-300">{deptInfo?.name || 'Grupo'}</p>
                                  {sector.description && <p className="text-xs text-slate-400 mt-1">{sector.description}</p>}
                                  <p className="text-xs text-cyan-300 mt-1">
                                    {format(bar.startDate, 'dd/MM/yyyy')} - {format(bar.endDate, 'dd/MM/yyyy')}
                                  </p>
                                </TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                            
                            {/* Barra da timeline */}
                            <div className="flex-1 relative h-6">
                              {/* Grade de fundo */}
                              <div className="absolute inset-0 flex">
                                {divisionLabels.map((_, idx) => (
                                  <div 
                                    key={idx}
                                    className={`flex-1 border-r border-slate-100 dark:border-slate-700/50 ${idx === 0 ? 'border-l' : ''}`}
                                  />
                                ))}
                              </div>
                              
                              {/* Barra do subgrupo */}
                              <div
                                className={`absolute top-1 h-4 rounded shadow-sm transition-all group-hover:shadow-md ${
                                  bar.isFuture 
                                    ? 'bg-gradient-to-r from-cyan-400 to-cyan-500' 
                                    : bar.isActive 
                                      ? 'bg-gradient-to-r from-green-400 to-green-500' 
                                      : bar.isPast 
                                        ? 'bg-slate-300 dark:bg-slate-600' 
                                        : 'bg-gradient-to-r from-blue-400 to-blue-500'
                                }`}
                                style={{ left: `${bar.left}%`, width: `${bar.width}%` }}
                              />
                            </div>
                          </div>
                        );
                      })
                    )}
                    
                    {/* Legenda */}
                    <div className="flex items-center gap-4 pt-2 border-t border-slate-200 dark:border-slate-700 text-[10px]">
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-2 rounded bg-gradient-to-r from-cyan-400 to-cyan-500" />
                        <span className="text-slate-500 dark:text-slate-400">Futuro</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-2 rounded bg-gradient-to-r from-green-400 to-green-500" />
                        <span className="text-slate-500 dark:text-slate-400">Ativo</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <div className="w-3 h-2 rounded bg-slate-300 dark:bg-slate-600" />
                        <span className="text-slate-500 dark:text-slate-400">Encerrado</span>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          )}
        </Card>
      )}

      {/* Projects Section */}
      <div>
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100">
            Projetos ({filteredProjects.length})
          </h2>
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar projetos..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9 w-48 dark:bg-slate-800 dark:border-slate-700"
              />
            </div>
            
            {/* Department Filter - apenas para admin/diretoria */}
            {canViewDepartments() && (
              <Select value={departmentFilter} onValueChange={(val) => { setDepartmentFilter(val); setSectorFilter('all'); }}>
                <SelectTrigger className="w-44 dark:bg-slate-800 dark:border-slate-700">
                  <Building className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Grupo" />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  <SelectItem value="all" className="dark:text-slate-200">Todos Grupos</SelectItem>
                  {departments.map(dept => (
                    <SelectItem key={dept.id} value={dept.id} className="dark:text-slate-200">
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Sector Filter - apenas para admin/diretoria quando há subgrupos */}
            {canViewDepartments() && filteredSectors.length > 0 && (
              <Select value={sectorFilter} onValueChange={setSectorFilter}>
                <SelectTrigger className="w-40 dark:bg-slate-800 dark:border-slate-700">
                  <Layers className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Subgrupo" />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  <SelectItem value="all" className="dark:text-slate-200">Todos Subgrupos</SelectItem>
                  {filteredSectors.map(sector => (
                    <SelectItem key={sector.id} value={sector.id} className="dark:text-slate-200">
                      {sector.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* View Mode Toggle */}
            <div className="flex items-center border rounded-lg overflow-hidden dark:border-slate-700">
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
                className="rounded-none"
              >
                <LayoutGrid className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
                className="rounded-none"
              >
                <List className="w-4 h-4" />
              </Button>
            </div>
            
            {isAdmin() && (
              <Dialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-project-button" className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Novo Projeto
                  </Button>
                </DialogTrigger>
                <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
                  <DialogHeader>
                    <DialogTitle className="dark:text-slate-100">Criar Projeto</DialogTitle>
                    <DialogDescription className="dark:text-slate-400">
                      Adicione um novo projeto ao sistema
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label htmlFor="project-name" className="dark:text-slate-300">Nome do Projeto</Label>
                    <Input
                      id="project-name"
                      data-testid="project-name-input"
                      value={newProject.name}
                      onChange={(e) => setNewProject({ ...newProject, name: e.target.value })}
                      placeholder="Ex: Website Redesign"
                      className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="project-dept" className="dark:text-slate-300">Grupo</Label>
                    <Select
                      value={newProject.department_id}
                      onValueChange={(value) => setNewProject({ ...newProject, department_id: value, sector_id: '' })}
                    >
                      <SelectTrigger data-testid="project-dept-select" className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                        <SelectValue placeholder="Selecione um grupo" />
                      </SelectTrigger>
                      <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                        {departments.map((dept) => (
                          <SelectItem key={dept.id} value={dept.id} className="dark:text-slate-200 dark:focus:bg-slate-700">
                            {dept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Subgrupo - aparece após selecionar grupo */}
                  <div className="space-y-2">
                    <Label htmlFor="project-sector" className="dark:text-slate-300 flex items-center gap-2">
                      <Layers className="w-4 h-4" />
                      Subgrupo (opcional)
                    </Label>
                    {newProject.department_id ? (
                      availableSectors.length > 0 ? (
                        <Select
                          value={newProject.sector_id || 'none'}
                          onValueChange={(value) => setNewProject({ ...newProject, sector_id: value === 'none' ? '' : value })}
                        >
                          <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                            <SelectValue placeholder="Selecione um subgrupo" />
                          </SelectTrigger>
                          <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                            <SelectItem value="none" className="dark:text-slate-200 dark:focus:bg-slate-700">
                              Nenhum subgrupo
                            </SelectItem>
                            {availableSectors.map((sector) => (
                              <SelectItem key={sector.id} value={sector.id} className="dark:text-slate-200 dark:focus:bg-slate-700">
                                {sector.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="text-sm text-slate-400 dark:text-slate-500 italic py-2">
                          Este grupo não possui subgrupos cadastrados
                        </p>
                      )
                    ) : (
                      <p className="text-sm text-slate-400 dark:text-slate-500 italic py-2">
                        Selecione um grupo primeiro
                      </p>
                    )}
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="project-desc" className="dark:text-slate-300">Descrição (opcional)</Label>
                    <Textarea
                      id="project-desc"
                      data-testid="project-desc-input"
                      value={newProject.description}
                      onChange={(e) => setNewProject({ ...newProject, description: e.target.value })}
                      placeholder="Descrição do projeto..."
                      className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                    />
                  </div>
                  
                  {/* Data de previsão */}
                  <div className="space-y-2">
                    <Label htmlFor="project-due-date" className="dark:text-slate-300 flex items-center gap-2">
                      <CalendarClock className="w-4 h-4" />
                      Previsão de Conclusão (opcional)
                    </Label>
                    <Input
                      id="project-due-date"
                      type="date"
                      value={newProject.due_date}
                      onChange={(e) => setNewProject({ ...newProject, due_date: e.target.value })}
                      className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                    />
                  </div>
                  
                  {/* User visibility selection */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2 dark:text-slate-300">
                      <Users className="w-4 h-4" />
                      Visibilidade do Projeto
                    </Label>
                    <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">
                      Selecione quais usuários podem ver este projeto. Se nenhum for selecionado, todos poderão ver.
                    </p>
                    <div className="border rounded-lg p-3 bg-slate-50 dark:bg-slate-900 dark:border-slate-600 max-h-40 overflow-y-auto">
                      {users.filter(u => u.role !== 'super_admin').length === 0 ? (
                        <p className="text-sm text-slate-500 dark:text-slate-400 text-center py-2">Nenhum usuário disponível</p>
                      ) : (
                        <div className="space-y-2">
                          {users.filter(u => u.role !== 'super_admin').map(u => (
                            <label 
                              key={u.id} 
                              className="flex items-center gap-2 cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800 p-1 rounded"
                            >
                              <Checkbox
                                checked={newProject.allowed_users.includes(u.id)}
                                onCheckedChange={() => toggleNewProjectUser(u.id)}
                              />
                              <span className="text-sm dark:text-slate-200">{u.full_name || u.email}</span>
                              <Badge variant="outline" className="text-xs ml-auto dark:border-slate-600 dark:text-slate-400">
                                {u.role === 'admin' ? 'Admin' : 'Usuário'}
                              </Badge>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    {newProject.allowed_users.length === 0 ? (
                      <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                        <Globe className="w-3 h-3" /> Visível para todos da empresa
                      </p>
                    ) : (
                      <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                        <Lock className="w-3 h-3" /> Restrito a {newProject.allowed_users.length} usuário(s)
                      </p>
                    )}
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setProjectDialogOpen(false)} className="dark:border-slate-600 dark:text-slate-300">
                    Cancelar
                  </Button>
                  <Button
                    data-testid="create-project-submit"
                    onClick={handleCreateProject}
                    disabled={creating}
                  >
                    {creating && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                    Criar
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
            )}
          </div>
        </div>

        {filteredProjects.length === 0 ? (
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardContent className="py-12 text-center">
              <FolderKanban className="w-16 h-16 mx-auto text-slate-300 dark:text-slate-600 mb-4" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
                {searchTerm || departmentFilter !== 'all' ? 'Nenhum projeto encontrado' : 'Nenhum projeto'}
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                {searchTerm || departmentFilter !== 'all' 
                  ? 'Tente mudar os filtros de busca'
                  : 'Comece criando seu primeiro projeto'}
              </p>
              {isAdmin() && departments.length === 0 && (
                <p className="text-sm text-amber-600 dark:text-amber-400">
                  Crie um grupo primeiro para poder adicionar projetos
                </p>
              )}
            </CardContent>
          </Card>
        ) : viewMode === 'list' ? (
          /* LIST VIEW */
          <Card className="dark:bg-slate-800/50 dark:border-slate-700">
            <CardContent className="pt-6">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50 dark:bg-slate-800/80 group">
                    <SortableHeader column="name" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Projeto</SortableHeader>
                    {canViewDepartments() && (
                      <SortableHeader column="department" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Grupo</SortableHeader>
                    )}
                    {canViewDepartments() && (
                      <SortableHeader column="sector" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Subgrupo</SortableHeader>
                    )}
                    <TableHead className="dark:text-slate-300">Visibilidade</TableHead>
                    <TableHead className="dark:text-slate-300">Flag</TableHead>
                    <SortableHeader column="due_date" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Previsão</SortableHeader>
                    <SortableHeader column="created_at" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort}>Criado em</SortableHeader>
                    <TableHead className="dark:text-slate-300 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredProjects.map(project => (
                    <TableRow 
                      key={project.id}
                      className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 group"
                      onClick={() => navigate(`/projects/${project.id}`)}
                    >
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-900 dark:text-slate-100">{project.name}</p>
                          {project.description && (
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-xs">
                              {project.description}
                            </p>
                          )}
                        </div>
                      </TableCell>
                      {canViewDepartments() && (
                        <TableCell>
                          {(() => {
                            const dept = departments.find(d => d.id === project.department_id);
                            const style = getDeptStyle(dept?.name || '');
                            const IconComponent = style.icon;
                            return (
                              <Badge variant="outline" className="text-xs dark:border-slate-600 dark:text-slate-300">
                                <IconComponent className="w-3 h-3 mr-1" />
                                {getDepartmentName(project.department_id)}
                              </Badge>
                            );
                          })()}
                        </TableCell>
                      )}
                      {canViewDepartments() && (
                        <TableCell>
                        {project.sector_id || project.sector_name ? (
                          <Badge variant="outline" className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-700">
                            <Layers className="w-3 h-3 mr-1" />
                            {project.sector_name || getSectorName(project.sector_id)}
                          </Badge>
                        ) : (
                          <span className="text-sm text-slate-400 dark:text-slate-500">—</span>
                        )}
                        </TableCell>
                      )}
                      <TableCell>
                        {project.allowed_users?.length > 0 ? (
                          <Badge variant="outline" className="text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-700">
                            <Lock className="w-3 h-3 mr-1" />
                            {project.allowed_users.length} usuário(s)
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-700">
                            <Globe className="w-3 h-3 mr-1" />
                            Todos
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                              <Button
                                variant="ghost"
                                size="icon"
                                className={`h-6 w-6 ${project.flag ? 'text-amber-500' : 'text-slate-400 hover:text-slate-600'}`}
                              >
                                <Flag className="w-3 h-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="start" className="w-44 dark:bg-slate-800 dark:border-slate-700">
                              <DropdownMenuItem 
                                onClick={(e) => handleUpdateProjectFlag(project.id, 'proxima_acao', e)}
                                className={`gap-2 text-sm dark:text-slate-200 ${project.flag === 'proxima_acao' ? 'bg-green-50 dark:bg-green-900/30' : ''}`}
                              >
                                <PlayCircle className="w-3.5 h-3.5 text-green-600" />
                                Próxima Ação
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={(e) => handleUpdateProjectFlag(project.id, 'aguardando_retorno', e)}
                                className={`gap-2 text-sm dark:text-slate-200 ${project.flag === 'aguardando_retorno' ? 'bg-amber-50 dark:bg-amber-900/30' : ''}`}
                              >
                                <Clock className="w-3.5 h-3.5 text-amber-600" />
                                Aguardando
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={(e) => handleUpdateProjectFlag(project.id, 'revisar', e)}
                                className={`gap-2 text-sm dark:text-slate-200 ${project.flag === 'revisar' ? 'bg-blue-50 dark:bg-blue-900/30' : ''}`}
                              >
                                <Eye className="w-3.5 h-3.5 text-blue-600" />
                                Revisar
                              </DropdownMenuItem>
                              {project.flag && (
                                <>
                                  <DropdownMenuSeparator className="dark:bg-slate-700" />
                                  <DropdownMenuItem 
                                    onClick={(e) => handleUpdateProjectFlag(project.id, null, e)}
                                    className="gap-2 text-sm text-slate-500 dark:text-slate-400"
                                  >
                                    <RotateCcw className="w-3.5 h-3.5" />
                                    Remover
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          {project.flag && (() => {
                            const flagInfo = getFlagInfo(project.flag);
                            const FlagIcon = flagInfo?.icon;
                            return flagInfo ? (
                              <Badge className={`text-[10px] px-1 py-0 ${flagInfo.color}`}>
                                <FlagIcon className="w-2.5 h-2.5" />
                              </Badge>
                            ) : null;
                          })()}
                        </div>
                      </TableCell>
                      <TableCell>
                        {project.due_date ? (
                          <span className={`text-sm flex items-center gap-1 ${
                            new Date(project.due_date) < new Date() 
                              ? 'text-red-500 dark:text-red-400' 
                              : 'text-slate-600 dark:text-slate-400'
                          }`}>
                            <CalendarClock className="w-3.5 h-3.5" />
                            {new Date(project.due_date).toLocaleDateString('pt-BR')}
                          </span>
                        ) : (
                          <span className="text-sm text-slate-400 dark:text-slate-500">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-slate-500 dark:text-slate-400">
                        {new Date(project.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 text-xs dark:border-slate-600 dark:text-slate-300"
                            onClick={() => navigate(`/projects/${project.id}/kanban`)}
                          >
                            <FolderKanban className="w-3.5 h-3.5 mr-1" />
                            Kanban
                          </Button>
                          {isAdmin() && (
                            <>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-blue-600"
                                onClick={(e) => openEditDialog(project, e)}
                              >
                                <Pencil className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteProject(project.id);
                                }}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        ) : (
          /* GRID VIEW */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <Card 
                key={project.id} 
                data-testid={`project-card-${project.id}`}
                className="card-hover group dark:bg-slate-800/50 dark:border-slate-700"
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg dark:text-slate-100">{project.name}</CardTitle>
                      <div className="flex items-center gap-2 mt-1">
                        {(() => {
                          const dept = departments.find(d => d.id === project.department_id);
                          const style = getDeptStyle(dept?.name || '');
                          const IconComponent = style.icon;
                          return (
                            <Badge variant="outline" className="text-xs dark:border-slate-600 dark:text-slate-400">
                              <IconComponent className="w-3 h-3 mr-1" />
                              {getDepartmentName(project.department_id)}
                            </Badge>
                          );
                        })()}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {project.allowed_users?.length > 0 ? (
                        <Badge variant="outline" className="text-xs bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border-amber-200 dark:border-amber-700">
                          <Lock className="w-3 h-3 mr-1" />
                          {project.allowed_users.length}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-700">
                          <Globe className="w-3 h-3 mr-1" />
                          Todos
                        </Badge>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 line-clamp-2">
                    {project.description || 'Sem descrição'}
                  </p>
                  
                  {/* Task Progress */}
                  {project.task_stats && (
                    <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-900/50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Progresso</span>
                        <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                          {project.task_stats.progress}%
                        </span>
                      </div>
                      <Progress value={project.task_stats.progress} className="h-2" />
                      <div className="flex items-center justify-between mt-2 text-xs text-slate-500 dark:text-slate-400">
                        <span className="flex items-center gap-1">
                          <CheckCircle2 className="w-3 h-3 text-green-500" />
                          {project.task_stats.completed} concluída(s)
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 text-amber-500" />
                          {project.task_stats.pending} pendente(s)
                        </span>
                      </div>
                    </div>
                  )}
                  
                  {/* View Options */}
                  <div className="flex items-center gap-2 mb-3">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 text-xs dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                      onClick={() => navigate(`/projects/${project.id}/kanban`)}
                    >
                      <FolderKanban className="w-3 h-3 mr-1" />
                      Kanban
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 text-xs dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                      onClick={() => navigate(`/tasks?project=${project.id}`)}
                    >
                      Lista/Tabela
                    </Button>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-slate-400 dark:text-slate-500">
                      Criado em {new Date(project.created_at).toLocaleDateString('pt-BR')}
                    </span>
                    <div className="flex items-center gap-1">
                      {isAdmin() && (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`edit-project-${project.id}`}
                            onClick={(e) => openEditDialog(project, e)}
                            className="opacity-0 group-hover:opacity-100 text-slate-600 dark:text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 h-8 w-8"
                            title="Editar projeto"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`permissions-project-${project.id}`}
                            onClick={(e) => openPermissionsDialog(project, e)}
                            className="opacity-0 group-hover:opacity-100 text-blue-600 dark:text-blue-400 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/30 h-8 w-8"
                            title="Gerenciar permissões"
                          >
                            <Settings2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`delete-project-${project.id}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteProject(project.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 text-red-600 dark:text-red-400 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 h-8 w-8"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Permissions Dialog */}
      <Dialog open={permDialogOpen} onOpenChange={setPermDialogOpen}>
        <DialogContent className="max-w-md dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-slate-100">
              <Settings2 className="w-5 h-5" />
              Permissões do Projeto
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              {selectedProject?.name} - Selecione quem pode visualizar este projeto
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="dark:text-slate-300">Usuários com acesso</Label>
              <Badge variant={selectedUsers.length === 0 ? 'default' : 'secondary'}>
                {selectedUsers.length === 0 ? 'Todos' : `${selectedUsers.length} selecionado(s)`}
              </Badge>
            </div>
            
            <ScrollArea className="h-64 border rounded-lg p-3 dark:border-slate-600">
              {users.filter(u => u.role !== 'super_admin').length === 0 ? (
                <p className="text-center text-slate-500 dark:text-slate-400 py-4">Nenhum usuário disponível</p>
              ) : (
                <div className="space-y-2">
                  {users.filter(u => u.role !== 'super_admin').map(u => (
                    <label 
                      key={u.id} 
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${
                        selectedUsers.includes(u.id) ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700' : 'hover:bg-slate-50 dark:hover:bg-slate-700'
                      }`}
                    >
                      <Checkbox
                        checked={selectedUsers.includes(u.id)}
                        onCheckedChange={() => toggleUserPermission(u.id)}
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium dark:text-slate-200">{u.full_name || u.email}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400">{u.email}</p>
                      </div>
                      {selectedUsers.includes(u.id) ? (
                        <Eye className="w-4 h-4 text-blue-500" />
                      ) : (
                        <EyeOff className="w-4 h-4 text-slate-300 dark:text-slate-600" />
                      )}
                    </label>
                  ))}
                </div>
              )}
            </ScrollArea>
            
            <div className="mt-3 p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
              {selectedUsers.length === 0 ? (
                <p className="text-sm text-green-700 dark:text-green-400 flex items-center gap-2">
                  <Globe className="w-4 h-4" />
                  <span>Projeto visível para <strong>todos</strong> os usuários da empresa</span>
                </p>
              ) : (
                <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  <span>Projeto <strong>restrito</strong> aos usuários selecionados + admins</span>
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPermDialogOpen(false)} className="dark:border-slate-600 dark:text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleSavePermissions} disabled={savingPerms}>
              {savingPerms && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Salvar Permissões
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Project Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="dark:text-slate-100">Editar Projeto</DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              Altere as informações do projeto
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="dark:text-slate-300">Nome do Projeto</Label>
              <Input
                value={editingProject?.name || ''}
                onChange={(e) => setEditingProject(prev => ({...prev, name: e.target.value}))}
                placeholder="Nome do projeto"
                className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="dark:text-slate-300">Descrição</Label>
              <Textarea
                value={editingProject?.description || ''}
                onChange={(e) => setEditingProject(prev => ({...prev, description: e.target.value}))}
                placeholder="Descrição do projeto"
                rows={3}
                className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="dark:text-slate-300">Grupo</Label>
              <Select
                value={editingProject?.department_id || ''}
                onValueChange={(value) => setEditingProject(prev => ({...prev, department_id: value}))}
              >
                <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                  <SelectValue placeholder="Selecione o grupo" />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id} className="dark:text-slate-200 dark:focus:bg-slate-700">
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="dark:border-slate-600 dark:text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Edit Department Dialog */}
      <Dialog open={editDeptDialogOpen} onOpenChange={setEditDeptDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-slate-100">
              <Building className="w-5 h-5" />
              Editar Grupo
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              Atualize as informações do grupo
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="dark:text-slate-300">Nome do Grupo</Label>
              <Input
                value={editingDept?.name || ''}
                onChange={(e) => setEditingDept(prev => ({...prev, name: e.target.value}))}
                placeholder="Nome do grupo"
                className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="dark:text-slate-300">Descrição (opcional)</Label>
              <Textarea
                value={editingDept?.description || ''}
                onChange={(e) => setEditingDept(prev => ({...prev, description: e.target.value}))}
                placeholder="Descrição do grupo..."
                rows={2}
                className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
              />
            </div>
            
            {/* Seletor de Cor */}
            <div className="space-y-2">
              <Label className="dark:text-slate-300">Cor</Label>
              <div className="flex flex-wrap gap-2">
                {DEPT_COLOR_OPTIONS.map((colorOpt) => (
                  <button
                    key={colorOpt.value}
                    type="button"
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      editingDept?.color === colorOpt.value 
                        ? 'border-slate-900 dark:border-white scale-110' 
                        : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: colorOpt.value }}
                    onClick={() => setEditingDept(prev => ({...prev, color: colorOpt.value}))}
                    title={colorOpt.name}
                  />
                ))}
              </div>
            </div>
            
            {/* Seletor de Ícone */}
            <div className="space-y-2">
              <Label className="dark:text-slate-300">Ícone</Label>
              <div className="flex flex-wrap gap-2 max-h-28 overflow-y-auto p-2 border rounded-md dark:border-slate-600">
                {DEPT_ICON_OPTIONS.map((iconOpt) => {
                  const IconComp = iconOpt.icon;
                  return (
                    <button
                      key={iconOpt.value}
                      type="button"
                      className={`w-8 h-8 rounded-lg flex items-center justify-center border transition-all ${
                        editingDept?.icon === iconOpt.value 
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-600' 
                          : 'border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400'
                      }`}
                      onClick={() => setEditingDept(prev => ({...prev, icon: iconOpt.value}))}
                      title={iconOpt.name}
                    >
                      <IconComp className="w-4 h-4" />
                    </button>
                  );
                })}
              </div>
            </div>
            
            {/* Preview */}
            <div className="space-y-2">
              <Label className="dark:text-slate-300">Prévia</Label>
              <div 
                className="flex items-center gap-2 p-3 rounded-lg border"
                style={{ borderColor: editingDept?.color || '#6B7280', backgroundColor: `${editingDept?.color || '#6B7280'}10` }}
              >
                {(() => {
                  const IconPreview = getIconComponent(editingDept?.icon);
                  return <IconPreview className="w-5 h-5" style={{ color: editingDept?.color || '#6B7280' }} />;
                })()}
                <span className="font-medium" style={{ color: editingDept?.color || '#6B7280' }}>
                  {editingDept?.name || 'Nome do Grupo'}
                </span>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDeptDialogOpen(false)} className="dark:border-slate-600 dark:text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleSaveDept} disabled={savingDept}>
              {savingDept && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Sector Dialog */}
      <Dialog open={editSectorDialogOpen} onOpenChange={setEditSectorDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-slate-100">
              <Layers className="w-5 h-5" />
              Editar Subgrupo
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              Atualize as informações do subgrupo
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="dark:text-slate-300">Nome do Subgrupo</Label>
              <Input
                value={editingSector?.name || ''}
                onChange={(e) => setEditingSector(prev => ({...prev, name: e.target.value}))}
                placeholder="Nome do subgrupo"
                className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="dark:text-slate-300">Descrição (opcional)</Label>
              <Textarea
                value={editingSector?.description || ''}
                onChange={(e) => setEditingSector(prev => ({...prev, description: e.target.value}))}
                placeholder="Descrição do subgrupo..."
                rows={2}
                className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="dark:text-slate-300">Grupo</Label>
              <Select
                value={editingSector?.department_id || ''}
                onValueChange={(val) => setEditingSector(prev => ({...prev, department_id: val}))}
              >
                <SelectTrigger className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100">
                  <SelectValue placeholder="Selecione o grupo" />
                </SelectTrigger>
                <SelectContent className="dark:bg-slate-800 dark:border-slate-700">
                  {departments.map((dept) => (
                    <SelectItem key={dept.id} value={dept.id} className="dark:text-slate-200">
                      {dept.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Datas de início e término (apenas admin) */}
            {isAdmin() && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="dark:text-slate-300 flex items-center gap-1">
                    <CalendarDays className="w-3.5 h-3.5" />
                    Data de Início
                  </Label>
                  <Input
                    type="date"
                    value={editingSector?.start_date || ''}
                    onChange={(e) => setEditingSector(prev => ({...prev, start_date: e.target.value}))}
                    className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="dark:text-slate-300 flex items-center gap-1">
                    <CalendarDays className="w-3.5 h-3.5" />
                    Data de Término
                  </Label>
                  <Input
                    type="date"
                    value={editingSector?.end_date || ''}
                    onChange={(e) => setEditingSector(prev => ({...prev, end_date: e.target.value}))}
                    className="dark:bg-slate-900 dark:border-slate-600 dark:text-slate-100"
                  />
                </div>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSectorDialogOpen(false)} className="dark:border-slate-600 dark:text-slate-300">
              Cancelar
            </Button>
            <Button onClick={handleSaveSector} disabled={savingSector}>
              {savingSector && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Delete Department Confirmation Dialog */}
      <Dialog open={deleteDeptDialogOpen} onOpenChange={setDeleteDeptDialogOpen}>
        <DialogContent className="dark:bg-slate-800 dark:border-slate-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600 dark:text-red-400">
              <Trash2 className="w-5 h-5" />
              Excluir Grupo
            </DialogTitle>
            <DialogDescription className="dark:text-slate-400">
              Tem certeza que deseja excluir o grupo <strong className="text-slate-900 dark:text-slate-200">{deptToDelete?.name}</strong>?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {deptToDelete?.project_count > 0 ? (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  ⚠️ Este grupo possui <strong>{deptToDelete.project_count}</strong> projeto(s) vinculado(s). 
                  Você precisa mover ou excluir os projetos antes de excluir o grupo.
                </p>
              </div>
            ) : (
              <div className="p-3 bg-slate-50 dark:bg-slate-900 rounded-lg">
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  Esta ação não pode ser desfeita. O grupo será permanentemente removido.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDeptDialogOpen(false)} className="dark:border-slate-600 dark:text-slate-300">
              Cancelar
            </Button>
            <Button 
              variant="destructive" 
              onClick={confirmDeleteDepartment} 
              disabled={deletingDept || (deptToDelete?.project_count > 0)}
            >
              {deletingDept && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectsPage;
